// src/app/core/services/payload-service.ts
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SummaryService } from './summary-service';
import { FormDataService } from './form-data-service';
import { getFlowFromUrl, Flow } from '../utils/flow-utils';
import { environment } from '../../../environments/environment';

export interface DomainSelection {
  domainName: string;
  years: number;
  tldPrice?: number;
  dnsType?: string;
}

interface BaseState {
  description: string;
  type: string;
  geography: string;
  businessType: string;
  currencyCode: string;
  serviceLevel: string;
}

@Injectable({ providedIn: 'root' })
export class PayloadService {
  private state$ = new BehaviorSubject<BaseState>({
    description: 'RETAIL_SIGNUP',
    type: 'EMAIL_APPS',
    geography: 'US',
    businessType: 'BUSINESS',
    currencyCode: 'USD',
    serviceLevel: 'INFRASTRUCTURE',
  });

  payload$!: Observable<any>;

  constructor(
    private summary: SummaryService,
    private formData: FormDataService,
    private router: Router
  ) {
    this.payload$ = combineLatest([
      this.state$,
      this.summary.orderItems$,
      this.summary.summary$,
      this.formData.accountFormData$,
      this.formData.addressFormData$,
      this.formData.cloudAccountData$,
      this.formData.cloudAddressData$,
      this.formData.captchaToken$,
      this.formData.deviceFingerPrint$,
      this.formData.rackUid$,
    ]).pipe(
      map(([s, items, sum, account, address, cloudAccount, cloudAddress, captchaToken, deviceFingerPrint, rackUid]) => {
        const domain = (this.summary as any)['domain$'].value as DomainSelection | null;
        const domainAmount = domain?.tldPrice ? Number(domain.tldPrice) : 0;

        const flow: Flow = getFlowFromUrl(this.router.url);
        const isReseller = flow === 'reseller';
        const isCloud = flow === 'cloud';
        const url = this.router.url;

        // ====================================================
        // 🌩️ CLOUD PAYLOAD
        // ====================================================
        if (isCloud) {
          const payload: any = {
            termsAndConditions: 'US',
            acceptTermsAndConditions: true,
            accountName: cloudAddress?.business && cloudAddress?.business.trim() !== "" ? cloudAddress?.business : `${cloudAddress?.firstName} ${cloudAddress?.lastName}`,
            contacts: {
              contact: [
                {
                  roles: { role: ['PRIMARY'] },
                  title: '',
                  suffix: '',
                  firstName: cloudAddress?.firstName,
                  lastName: cloudAddress?.lastName,
                  addresses: {
                    address: [
                      {
                        primary: true,
                        street: cloudAddress?.street,
                        city: cloudAddress?.city,
                        statecode: cloudAddress?.state,
                        zipcode: cloudAddress?.postalCode,
                        country: cloudAddress?.country,
                      },
                    ],
                  },
                  user: {
                    password: cloudAccount?.password,
                    username: cloudAccount?.username,
                  },
                  emailAddresses: {
                    emailAddress: [{ primary: true, address: cloudAccount?.email }],
                  },
                  phoneNumbers: {
                    phoneNumber: [
                      {
                        category: 'HOME',
                        country: cloudAddress?.country || 'US',
                        number: (cloudAddress?.phone || '').replace(/[^\d]/g, '').trim(),
                        primary: true,
                      },
                    ],
                  },
                },
              ],
            },
            description: 'RETAIL_SIGNUP',
            type: 'CLOUD',
            geography: 'US',
            businessType: 'BUSINESS',
            vatCode: '',
            promoCode: '',
            serviceLevel: 'INFRASTRUCTURE',
            affiliateCodeAndType: { code: '', type: '' },
            currencyCode: 'USD',
            referralCode: '',
            metadata: {
              property: [
                { key: 'deviceFingerPrint', value: deviceFingerPrint || '' },
                { key: 'kaptchaSessionID', value: deviceFingerPrint || '' },
                { key: 'remoteReferer', value: '' },
                { key: 'assisted_signup', value: 'no' },
                { key: 'skipCloudWelcomeEmail', value: 'true' },
                { key: 'acceptPromoTermsAndConditions', value: 'false' },
                ...(rackUid ? [{ key: 'rackUID', value: rackUid }] : []),
                { key: 'IS_UASrackuid', value: `US${deviceFingerPrint}` },
                ...(captchaToken ? [{ key: 'rToken', value: captchaToken }] : []),
                { key: 'cloudSitesPurchased', value: 'false' },
                { key: 'cloudFilesPurchased', value: 'false' },
                { key: 'cloudServersPurchased', value: 'true' },
                { key: 'loadBalancersPurchased', value: 'false' },
              ],
            },
            postbackURL: this.getPostbackUrl(),
            serviceType: 'SYSOPS',
          };

          if (url.includes('sddc-flex')) {
            payload.externalId = 'RSVC';
            payload.originUrl =
              'https://manage.rackspace.com/service-catalog/en/platform-signup/?platform=sddc-flex#auth';
          } else {
            payload.externalId = 'OS_FLEX';
            payload.originUrl =
              'https://manage.rackspace.com/service-catalog/en/platform-signup/?platform=openstack-flex#auth';
          }
          return payload;
        }

        // ====================================================
        // 📨 EMAIL_APPS / OFFICE365 / DEFAULT PAYLOAD
        // ====================================================
        
        return {
          termsAndConditions: 'US',
          acceptTermsAndConditions: true,
          accountName: account?.business && account?.business.trim() !== "" ? account?.business : `${account?.firstName} ${account?.lastName}`,
          contacts: {
            contact: [
              {
                roles: { role: ['PRIMARY'] },
                title: '',
                suffix: '',
                firstName: account?.firstName,
                lastName: account?.lastName,
                addresses: {
                  address: [
                    {
                      primary: true,
                      street: address?.street,
                      city: address?.city,
                      statecode: address?.state,
                      zipcode: address?.postalCode,
                      country: address?.country,
                    },
                  ],
                },
                user: {
                  password: account?.password,
                  username: account?.username,
                  secretQA: {
                    answer: account?.securityAnswer,
                    question: account?.securityQuestion,
                  },
                },
                emailAddresses: {
                  emailAddress: [{ primary: true, address: account?.email }],
                },
                phoneNumbers: {
                  phoneNumber: [
                    {
                      category: 'HOME',
                      country: address?.country,
                      number: (account?.phone || '').replace(/[^\d]/g, '').trim(),
                      primary: true,
                    },
                  ],
                },
              },
            ],
          },
          description: s.description,
          type: s.type,
          geography: s.geography,
          businessType: s.businessType,
          vatCode: '',
          promoCode: '',
          serviceLevel: s.serviceLevel,
          affiliateCodeAndType: { code: '', type: '' },
          currencyCode: s.currencyCode,
          referralCode: '',
          metadata: {
            property: [
              { key: 'totalAmount', value: sum.totalMonthly.toFixed(2) },
              ...(deviceFingerPrint ? [{ key: 'deviceFingerPrint', value: deviceFingerPrint }] : []),
              { key: 'kaptchaSessionID', value: '' },
              ...(captchaToken ? [{ key: 'rToken', value: captchaToken }] : []),
              ...(rackUid ? [{ key: 'rackUID', value: rackUid }] : []),
            ],
          },
          order: {
            id: '',
            item: sum.items.map(it => {
              if (it.id === 'RACKSPACE_MAILBOX') {
                return {
                  offeringId: 'RACKSPACE_EMAIL',
                  productId: it.id,
                  quantity: String(it.quantity),
                };
              }
              if (it.kind === 'addon' && flow === 'apps') {
                return { offeringId: it.id, productId: it.id, quantity: '1' };
              }
              if (flow === 'office365' && (it.kind === 'o365' || it.kind === 'addon')) {
                return {
                  offeringId: 'O365',
                  productId: it.offerId ?? it.id,
                  quantity: String(it.quantity),
                };
              }
              return { offeringId: it.id, productId: it.id, quantity: String(it.quantity) };
            }),
            metadata: {
              property: [
                ...(isReseller ? [{ key: 'type', value: 'reseller' }] : []),
                { key: 'domainName', value: domain?.domainName || (account as any)?.domain || '' },
                { key: 'dnsType', value: domain?.dnsType || 'pointtous' },
                { key: 'domainRegistrationPrice', value: domain?.tldPrice?.toFixed(2) || '' },
                { key: 'domainYears', value: domain?.years?.toString() || '1' },
                { key: 'nexus', value: '' },
                { key: 'purpose', value: '' },
                { key: 'us_country', value: '' },
                ...(flow === 'office365'
                  ? [{ key: 'O365DomainPrefix', value: account?.domain?.trim() || '' }]
                  : [{ key: 'microsoftDomainPrefix', value: '' }]),
                ...(domainAmount ? [{ key: 'totalAmount', value: domainAmount.toFixed(2) }] : []),
              ],
            },
          },
          postbackURL: this.getPostbackUrl(),
        };
      })
    );
  }

  private getPostbackUrl(): string {
    const host = window.location.hostname;
    if (environment.production) return 'https://cart.rackspace.com/status';
    if (host.includes('stg') || host.includes('stage')) return 'https://new-cart-stg.rackspace.com/status';
    if (host.includes('cartl.rackspace.com')) return 'https://cartl.rackspace.com/status';
    return 'https://new-cart-stg.rackspace.com/status';
  }
}
